/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import Layout from "../../../layout";
import Card from "../../../components/Card";
import BackButton from "../../../components/BackButton";
import httpRequest from "../../../ajax";
import { SectionLoader } from "../../../components/PageLoader";
import { useSelector } from "react-redux";
import InvoiceIcon from "../../../assets/icons/invoice-icon.svg";
import CustomButton from "../../../components/CustomButton";

/* ─── status badge colours ───────────────────────────────────── */
const statusColors = {
  paid:    { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
  pending: { bg: "#FFF3CD", color: "#8B6200", border: "#FFD875" },
  overdue: { bg: "#FFE4E6", color: "#9F1239", border: "#FDA4AF" },
  "1":     { bg: "#EEF4FF", color: "#3A6ED4", border: "#BFCFFF" },
};
const getStatusStyle = (status = "") => {
  const s = String(status).toLowerCase();
  return statusColors[s] || { bg: "#F0F3F6", color: "#4A5568", border: "#CBD5E0" };
};

/* ─── reusable field box ─────────────────────────────────────── */
const Field = ({ label, value, mono = false }) => (
  <div style={{ width: "calc(50% - 16px)" }}>
    <div style={{
      fontSize: "11px",
      fontWeight: "700",
      color: "#1E2329",
      marginBottom: "6px",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {label}
    </div>
    <div style={{
      padding: "10px 14px",
      border: "1px solid #D8DDE3",
      borderRadius: "4px",
      fontSize: "13px",
      color: !value || value === "N/A" ? "#9BA8B4" : "#1E2329",
      fontStyle: !value || value === "N/A" ? "italic" : "normal",
      fontFamily: mono ? "monospace" : "'DM Sans', sans-serif",
      letterSpacing: mono ? "0.03em" : "normal",
      background: "#fff",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
      // ADD THESE:
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
      lineHeight: "20px",
      paddingTop: "10px",
    }}>
      {value || "N/A"}
    </div>
  </div>
);
const getHumanInvoiceTypeLabel = (inv) => {
  if (!inv || !inv.payment_type) return "N/A";
  if (inv.payment_type === "job_seeker_subscription") return "Workbrook Pro subscription";
  if (inv.payment_type === "initial") return "Part Payment";
  if (inv.payment_type === "final") return "Full Payment";
  if (inv.payment_type === "ppv") return "Pay per view";
  if (inv.payment_type === "per_view") return "Per view";
  return inv.payment_type;
};

/* ─── main component ─────────────────────────────────────────── */
const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const history = useHistory();
  const [invoice, setInvoice] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userData } = useSelector((state) => state.auth);
  const [invoiceData, setInvoiceData] = useState({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpRequest("get", `/jobs/invoices/${invoiceId}`);
        const responseData = response.data.data;
        setInvoiceData(responseData);
        if (response.status && response.data) {
          setInvoice(responseData);
        } else {
          setError("Invoice not found");
        }
      } catch (err) {
        console.error("Failed to fetch invoice:", err);
        setError("Failed to load invoice details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceDetails();
  }, [invoiceId]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <SectionLoader />
      </div>
    );
  }
  if (error) return <div className="p-4 text-danger">{error}</div>;

  const handleDownload = () => {
    try {
      setDownloading(true);

      const { jsPDF } = window.jspdf;
      if (!jsPDF) {
        alert("PDF library not loaded. Please refresh and try again.");
        return;
      }

      if (invoice.payment_type === "job_seeker_subscription") {
        const doc = new jsPDF("p", "mm", "a4");
        const PW = 210;
        const PH = 297;
        const M = 20;
        const CW = PW - M * 2;
        const PRIMARY = [3, 169, 244];
        const DARK = [3, 34, 80];
        const WHITE = [255, 255, 255];
        const LIGHT = [240, 248, 255];
        const BODY = [60, 72, 88];
        const MUTED = [140, 155, 170];

        doc.setFillColor(...PRIMARY);
        doc.rect(0, 0, PW, 18, "F");
        doc.setTextColor(...WHITE);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text("WORKBROOK", M, 12);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text("workbrook.com", PW - M, 12, { align: "right" });

        let y = 30;
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...DARK);
        doc.text("SUBSCRIPTION RECEIPT", M, y);
        y += 12;

        doc.setFillColor(...LIGHT);
        doc.roundedRect(M, y, CW, 28, 3, 3, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...PRIMARY);
        doc.text("INVOICE NO.", M + 5, y + 7);
        doc.text("DATE", M + 55, y + 7);
        doc.text("AMOUNT", M + CW - 5, y + 7, { align: "right" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...DARK);
        doc.text(String(invoice.invoice_number || "N/A"), M + 5, y + 16);
        doc.text(
          invoice.created_at ? invoice.created_at.split(" ")[0] : "N/A",
          M + 55,
          y + 16,
        );
        const amt =
          invoice.currency && invoice.amount != null
            ? `${invoice.currency} ${Number(invoice.amount).toLocaleString()}`
            : "N/A";
        doc.text(amt, M + CW - 5, y + 16, { align: "right" });
        y += 36;

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...MUTED);
        doc.text("PLAN", M, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...BODY);
        doc.text(String(invoice.subscription_plan_name || "Workbrook Pro"), M, y + 6);
        y += 14;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...MUTED);
        doc.text("TIER", M, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...BODY);
        doc.text(String(invoice.subscription_tier || "—"), M, y + 6);
        y += 14;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...MUTED);
        doc.text("TRANSACTION ID", M, y);
        doc.setFont("courier", "normal");
        doc.setTextColor(...BODY);
        doc.text(String(invoice.transaction_id || "N/A"), M, y + 6);
        y += 18;

        doc.setFontSize(7.5);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...MUTED);
        doc.text(
          "Workbrook Pro subscription — wallet charge. Retain for your records.",
          M,
          y,
        );

        doc.setFillColor(...PRIMARY);
        doc.rect(0, PH - 12, PW, 12, "F");
        doc.setTextColor(...WHITE);
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "normal");
        const dateStr = new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        doc.text(`Generated by Workbrook  ·  ${dateStr}`, PW / 2, PH - 4.5, {
          align: "center",
        });

        doc.save(`Invoice-${invoice.invoice_number || invoiceId}.pdf`);
        return;
      }

      const doc = new jsPDF("p", "mm", "a4");
    const PW = 210;
    const PH = 297;
    const M  = 20;
    const CW = PW - M * 2;

    // ── Colors ────────────────────────────────────────────────────────────────
    const PRIMARY   = [3, 169, 244];    // #03A9F4
    const DARK      = [3,  34,  80];    // #032250
    const WHITE     = [255, 255, 255];
    const LIGHT     = [240, 248, 255];  // very light blue tint
    const BODY      = [60,  72,  88];
    const MUTED     = [140, 155, 170];
    const DIVIDER   = [225, 232, 240];

    // ── HEADER STRIP ─────────────────────────────────────────────────────────
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, PW, 18, "F");

    doc.setTextColor(...WHITE);
    doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("WORKBROOK", M, 12);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("workbrook.com", PW - M, 12, { align: "right" });

    let y = 30;

    // ── INVOICE HEADING ROW ───────────────────────────────────────────────────
    doc.setFontSize(22); doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("INVOICE", M, y);

    // Status pill — right aligned
    const status = (invoice.status || "pending").toLowerCase();
    const statusMap = {
      paid:    { bg: [209,250,229], fg: [6,95,70],   border: [110,231,183], label: "PAID"    },
      pending: { bg: [255,243,205], fg: [139,98,0],  border: [255,216,117], label: "PENDING" },
      overdue: { bg: [255,228,230], fg: [159,18,57], border: [253,164,175], label: "OVERDUE" },
    };
    const sc = statusMap[status] || { bg: [240,243,246], fg: [74,85,104], border: [203,213,224], label: status.toUpperCase() };
    const bW = 28, bH = 9;
    doc.setFillColor(...sc.bg);
    doc.roundedRect(PW - M - bW, y - 8, bW, bH, bH/2, bH/2, "F");
    doc.setDrawColor(...sc.border); doc.setLineWidth(0.3);
    doc.roundedRect(PW - M - bW, y - 8, bW, bH, bH/2, bH/2, "S");
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...sc.fg);
    doc.text(sc.label, PW - M - bW/2, y - 2.8, { align: "center" });

    y += 8;

    // ── FULL-WIDTH DIVIDER ────────────────────────────────────────────────────
    doc.setDrawColor(...PRIMARY); doc.setLineWidth(0.6);
    doc.line(M, y, M + CW, y);
    y += 8;

    // ── INFO ROW (4 cols in one light band) ───────────────────────────────────
    doc.setFillColor(...LIGHT);
    doc.roundedRect(M, y, CW, 18, 3, 3, "F");

    const infoItems = [
      { label: "Invoice No.",  value: invoice.invoice_number || "N/A" },
      { label: "Date Issued",  value: invoice.created_at ? invoice.created_at.split(" ")[0] : "N/A" },
      { label: "Due Date",     value: invoice.locked_until ? invoice.locked_until.split(" ")[0] : "N/A" },
      { label: "Type", value: getHumanInvoiceTypeLabel(invoice) },
    ];

    const colW4 = CW / 4;
    infoItems.forEach((item, i) => {
      const ix = M + i * colW4 + 5;
      doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
      doc.setTextColor(...PRIMARY);
      doc.text(item.label.toUpperCase(), ix, y + 6);
      doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
      doc.setTextColor(...DARK);
      doc.text(item.value, ix, y + 13);
      if (i < 3) {
        doc.setDrawColor(...DIVIDER); doc.setLineWidth(0.2);
        doc.line(M + (i+1)*colW4, y + 3, M + (i+1)*colW4, y + 15);
      }
    });
    y += 26;

    // ── SECTION LABEL HELPER ──────────────────────────────────────────────────
    const sectionLabel = (text, sy) => {
      doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.setTextColor(...PRIMARY);
      doc.text(text, M, sy);
      doc.setDrawColor(...PRIMARY); doc.setLineWidth(0.3);
      doc.line(M, sy + 1.5, M + CW, sy + 1.5);
    };

    // ── LINE ITEMS TABLE ──────────────────────────────────────────────────────
    sectionLabel("INVOICE DETAILS", y);
    y += 8;

    // Table header
    doc.setFillColor(...DARK);
    doc.rect(M, y, CW, 9, "F");
    doc.setTextColor(...WHITE); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
    doc.text("DESCRIPTION",  M + 4,       y + 6);
    doc.text("JOB ID",       M + 80,      y + 6);
    doc.text("SALARY",       M + 105,     y + 6);
    doc.text("FEE %",        M + 135,     y + 6);
    doc.text("AMOUNT",       M + CW - 4,  y + 6, { align: "right" });
    y += 13;

    // Single data row
    doc.setFillColor(...LIGHT);
    doc.rect(M, y, CW, 12, "F");
    doc.setTextColor(...DARK); doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text("Placement Fee", M + 4, y + 5);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...BODY);
    doc.text("Recruitment service", M + 4, y + 10);

    doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...BODY);
    doc.text(String(invoice.job_id || "N/A"), M + 80, y + 7.5);

    const salaryValue = invoice.confirmed_salary || invoice.estimated_salary;
    const salary = salaryValue
      ? `${invoice.currency || 'NGN'} ${Number(salaryValue).toLocaleString()}`
      : "N/A";
    doc.text(salary, M + 105, y + 7.5);

    doc.text(invoice.percentage != null ? `${invoice.percentage}%` : "N/A", M + 135, y + 7.5);

    const amount = invoice.currency && invoice.amount
      ? `${invoice.currency} ${Number(invoice.amount).toLocaleString()}`
      : "N/A";
    doc.setFont("helvetica", "bold"); doc.setTextColor(...DARK);
    doc.text(amount, M + CW - 4, y + 7.5, { align: "right" });
    y += 16;

    // Bottom table border
    doc.setDrawColor(...DIVIDER); doc.setLineWidth(0.3);
    doc.line(M, y, M + CW, y);
    y += 6;

    // ── TOTAL ROW ─────────────────────────────────────────────────────────────
    doc.setFillColor(...PRIMARY);
    doc.roundedRect(M + CW - 70, y, 70, 14, 2, 2, "F");
    doc.setTextColor(...WHITE);
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
    doc.text("TOTAL DUE", M + CW - 35, y + 5.5, { align: "center" });
    doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(amount, M + CW - 35, y + 11.5, { align: "center" });
    y += 22;

    // ── TWO-COL DETAILS ROW ───────────────────────────────────────────────────
    sectionLabel("ADDITIONAL DETAILS", y);
    y += 8;

    const detailRows = [
      ["Transaction ID",   invoice.transaction_id || "N/A"],
      ["Date Updated",     invoice.updated_at     || "N/A"],
    ];

    detailRows.forEach(([label, value]) => {
      // label
      doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...MUTED);
      doc.text(label, M, y);
      // value
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...BODY);
      doc.text(value, M + 45, y);
      // dotted line between
      doc.setDrawColor(...DIVIDER); doc.setLineWidth(0.2);
      doc.setLineDashPattern([0.5, 1], 0);
      doc.line(M + 38, y - 0.5, M + 43, y - 0.5);
      doc.setLineDashPattern([], 0);
      y += 8;
    });

    y += 4;

    // ── NOTE STRIP ────────────────────────────────────────────────────────────
    doc.setFillColor(240, 248, 255);
    doc.roundedRect(M, y, CW, 12, 2, 2, "F");
    doc.setDrawColor(...PRIMARY); doc.setLineWidth(0.25);
    doc.roundedRect(M, y, CW, 12, 2, 2, "S");
    doc.setFontSize(7.5); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED);
    doc.text(
      "This invoice was generated automatically by the Workbrook platform. Please retain for your records.",
      PW / 2, y + 7.5, { align: "center" }
    );
    y += 20;

    // ── FOOTER ────────────────────────────────────────────────────────────────
    doc.setFillColor(...PRIMARY);
    doc.rect(0, PH - 12, PW, 12, "F");
    doc.setTextColor(...WHITE);
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    doc.text(`Generated by Workbrook  ·  ${dateStr}`, PW / 2, PH - 4.5, { align: "center" });

    doc.save(`Invoice-${invoice.invoice_number || invoiceId}.pdf`);

  } catch (err) {
    console.error("Failed to generate PDF:", err);
    alert("Failed to generate PDF. Please try again.");
  } finally {
    setDownloading(false);
  }
};

  const handleViewPDF = async () => {
    try {
      setDownloading(true);
      const response = await httpRequest("get", `/jobs/invoices/${invoiceId}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Failed to view invoice:", error);
      alert("Failed to view invoice. Please try again later.");
    } finally {
      setDownloading(false);
    }
  };

  const hasData = Object.keys(invoiceData).length > 0;
  const ss = getStatusStyle(invoice.status);
  const isSubscription = invoice.payment_type === "job_seeker_subscription";

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .wb-invoice * { box-sizing: border-box; }
      `}</style>

      <div className="wb-invoice m-3">
        <Card noPadding additionalClass="p-0 mb-4">
          <div style={{ display: "flex", flexDirection: "column", fontFamily: "'DM Sans', sans-serif" }}>

            {/* ── TOP NAV BAR ── */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 20px",
              borderBottom: "1px solid #E8ECF0",
              background: "#fff",
            }}>
              <BackButton />
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <img src={InvoiceIcon} alt="Workbrook" style={{ width: "108px", height: "108px" }} />
               
              </div>
            </div>

            {/* ── PAGE BACKGROUND ── */}
            <div style={{ padding: "32px 40px 40px", background: "#F7F9FB" }}>
              <div style={{
                background: "#fff",
                border: "1px solid #E0E5EB",
                borderRadius: "6px",
                overflow: "hidden",
                maxWidth: "900px",
                margin: "0 auto",
              }}>

                {/* ── DOCUMENT TITLE + DOWNLOAD ── */}
                <div style={{ padding: "28px 32px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                     
                        <span style={{
                          fontSize: "17px",
                          fontWeight: "800",
                          color: "#1E2329",
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                        }}>
                          INVOICE 
                        </span>
                      </div>
                      {/* <p style={{ fontSize: "12px", color: "#8A96A3", margin: 0 }}>
                        A clean internal template for placement-fee invoices and payment tracking.
                      </p> */}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {/* <CustomButton
                        handleClick={handleViewPDF}
                        text={downloading ? "Loading..." : "View PDF"}
                        backgroundColor="#fff"
                        color="#2C2C2C"
                        disabled={downloading}
                        style={{ 
                          fontSize: "12px", 
                          fontWeight: "600", 
                          whiteSpace: "nowrap",
                          border: "1px solid #D8DDE3",
                          opacity: downloading ? 0.6 : 1,
                          cursor: downloading ? "not-allowed" : "pointer"
                        }}
                      /> */}
                      <CustomButton
                        handleClick={handleDownload}
                        text={downloading ? "Downloading..." : "Download Invoice"}
                        backgroundColor="#2C2C2C"
                        color="#F5F5F5"
                        disabled={downloading}
                        style={{ 
                          fontSize: "12px", 
                          fontWeight: "600", 
                          whiteSpace: "nowrap",
                          opacity: downloading ? 0.6 : 1,
                          cursor: downloading ? "not-allowed" : "pointer"
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* ── SUMMARY HEADER ROW ── */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  borderTop: "1px solid #E0E5EB",
                  borderBottom: "1px solid #E0E5EB",
                }}>
                  {[
                    { label: "Invoice Number", value: invoice.invoice_number, mono: true },
                    { label: "Status",         value: invoice.status,         isStatus: true },
                    { label: "Invoice Type",   value: getHumanInvoiceTypeLabel(invoice) },
                  ].map(({ label, value, mono, isStatus }, i) => (
                    <div
                      key={label}
                      style={{
                        padding: "14px 20px",
                        borderRight: i < 2 ? "1px solid #E0E5EB" : "none",
                        background: "#F0F4F8",
                      }}
                    >
                      <div style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        color: "#8A96A3",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        marginBottom: "6px",
                      }}>
                        {label}
                      </div>
                      {isStatus ? (
                        <span style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          background: ss.bg,
                          color: ss.color,
                          border: `1px solid ${ss.border}`,
                        }}>
                          {value || "N/A"}
                        </span>
                      ) : (
                        <span style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: value ? "#1E2329" : "#9BA8B4",
                          fontStyle: !value ? "italic" : "normal",
                          fontFamily: mono ? "monospace" : "inherit",
                          letterSpacing: mono ? "0.03em" : "normal",
                        }}>
                          {value || "[Enter invoice number]"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* ── FIELDS ── */}
                {hasData ? (
                  <div style={{ padding: "28px 32px 8px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "20px 32px" }}>
                      {isSubscription ? (
                        <>
                          <Field
                            label="Plan"
                            value={invoice.subscription_plan_name || "Workbrook Pro"}
                          />
                          <Field
                            label="Tier"
                            value={invoice.subscription_tier || null}
                          />
                          {invoice.job_seeker_subscription_id ? (
                            <Field
                              label="Subscription ID"
                              value={String(invoice.job_seeker_subscription_id)}
                              mono
                            />
                          ) : null}
                          <Field
                            label="Total paid"
                            value={
                              invoice.currency && invoice.amount != null
                                ? `${invoice.currency} ${invoice.amount}`
                                : null
                            }
                          />
                          <Field label="Date created" value={invoice.created_at} />
                          <Field label="Date updated" value={invoice.updated_at} />
                          <Field label="Transaction ID" value={invoice.transaction_id} mono />
                        </>
                      ) : (
                        <>
                          <Field label="Job ID"                   value={String(invoice.job_id || "")}   mono />
                          <Field label="Date Updated"             value={invoice.updated_at} />
                          <Field label="Date Created"             value={invoice.created_at} />
                          <Field label="Placement Fee %"          value={invoice.percentage != null ? String(invoice.percentage) : null} />
                          <Field label="Salary"                   value={invoice.confirmed_salary != null ? String(invoice.confirmed_salary) : (invoice.estimated_salary != null ? String(invoice.estimated_salary) : null)} />
                          <Field label="Total Invoice Amount Paid" value={invoice.currency && invoice.amount ? `${invoice.currency} ${invoice.amount}` : null} />
                          <Field label="Locked Until"             value={invoice.locked_until} />
                          <Field label="Transaction ID"           value={invoice.transaction_id}         mono />
                        </>
                      )}
                    </div>

                    {/* footer note */}
                    <div style={{
                      marginTop: "24px",
                      paddingTop: "16px",
                      borderTop: "1px solid #F0F3F6",
                      fontSize: "11px",
                      color: "#9BA8B4",
                      lineHeight: "1.7",
                      paddingBottom: "8px",
                    }}>
                      {/* Field order prioritises invoice identity first, then audit dates, compensation basis, payment amounts, and settlement tracking details. */}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: "40px", textAlign: "center", color: "#9BA8B4", fontSize: "13px" }}>
                    No invoice data found.
                  </div>
                )}

              </div>
            </div>

          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default InvoiceDetail;